import React from 'react';
import { FileCode, Upload, Image, Link2, FileText, File, Lightbulb, Pin } from 'lucide-react';
import Card from '../../../components/Card/Card';
import SectionHeader from '../../../components/SectionHeader/SectionHeader';

function TemplatesWordTab() {
  return (
    <>
      {/* Section: Introduction */}
      <Card className="card-large">
        <SectionHeader icon={FileCode} title="Templates Word personnalisés" variant="default" />
        <div className="guide-section-content">
          <div className="guide-info">
            <File className="guide-info-icon" size={20} />
            <p>
              Un <strong>template Word</strong> définit la mise en page de vos rapports exportés :
              en-tête, logo, pied de page, polices, marges. Créez des documents professionnels
              à votre image.
            </p>
          </div>
        </div>
      </Card>

      {/* Section: Template général */}
      <Card className="card-large">
        <SectionHeader icon={Pin} title="Template général (par défaut)" variant="success" />
        <div className="guide-section-content">
          <div className="guide-info">
            <Pin className="guide-info-icon" size={20} />
            <p>
              Le <strong>template général</strong> est appliqué automatiquement à <strong>toutes vos exportations Word</strong>.
              Il suffit de l'uploader une fois : tous vos rapports auront votre en-tête, logo et pied de page.
            </p>
          </div>
          <div className="guide-tip">
            <Lightbulb className="guide-tip-icon" size={20} />
            <p>
              C'est la solution la plus simple. Créez un document Word avec votre mise en page,
              uploadez-le comme template général, et c'est terminé. Tous vos exports Word auront ce style.
            </p>
          </div>
        </div>
      </Card>

      {/* Section: Template lié à un modèle */}
      <Card className="card-large">
        <SectionHeader icon={Link2} title="Template lié à un modèle (avancé)" variant="info" />
        <div className="guide-section-content">
          <div className="guide-info">
            <Link2 className="guide-info-icon" size={20} />
            <p>
              Pour des besoins plus spécifiques, vous pouvez créer un <strong>template dédié à un type de rapport</strong>.
              Par exemple : un template "Écho" avec un tableau de mesures pré-formaté,
              ou un template "Consultation" avec une mise en page différente.
            </p>
          </div>
          <div className="guide-features">
            <div className="guide-feature">
              <div className="guide-feature-icon purple">
                <FileText size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Tableaux complexes</h4>
                <p>Intégrez des tableaux pré-formatés pour vos mesures</p>
              </div>
            </div>
            <div className="guide-feature">
              <div className="guide-feature-icon cyan">
                <FileText size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Listes structurées</h4>
                <p>Créez des listes avec puces ou numérotation spécifique</p>
              </div>
            </div>
            <div className="guide-feature">
              <div className="guide-feature-icon brown">
                <Image size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Mise en page unique</h4>
                <p>Un design différent selon le type de rapport</p>
              </div>
            </div>
          </div>
          <div className="guide-tip">
            <Lightbulb className="guide-tip-icon" size={20} />
            <p>
              Si un rapport a un template lié, celui-ci est utilisé à la place du template général.
              Sinon, le template général s'applique.
            </p>
          </div>
        </div>
      </Card>

      {/* Section: Créer un template */}
      <Card className="card-large">
        <SectionHeader icon={Upload} title="Ajouter un template" variant="default" />
        <div className="guide-section-content">
          <div className="guide-steps">
            <div className="guide-step">
              <span className="guide-step-number">1</span>
              <div className="guide-step-content">
                <h4>Créez votre template dans Word</h4>
                <p>Ajoutez votre en-tête, logo, coordonnées, pied de page.</p>
              </div>
            </div>
            <div className="guide-step">
              <span className="guide-step-number">2</span>
              <div className="guide-step-content">
                <h4>Sauvegardez en .docx</h4>
                <p>Enregistrez le fichier au format Word (.docx).</p>
              </div>
            </div>
            <div className="guide-step">
              <span className="guide-step-number">3</span>
              <div className="guide-step-content">
                <h4>Allez dans Accueil → Templates</h4>
                <p>Uploadez votre fichier dans Voxia.</p>
              </div>
            </div>
            <div className="guide-step">
              <span className="guide-step-number">4</span>
              <div className="guide-step-content">
                <h4>Associez à un modèle (optionnel)</h4>
                <p>Liez le template à un ou plusieurs types de rapport, ou définissez-le comme général.</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Section: Fonctionnalités */}
      <Card className="card-large">
        <SectionHeader icon={Image} title="Ce que vous pouvez personnaliser" variant="success" />
        <div className="guide-section-content">
          <div className="guide-features">
            <div className="guide-feature">
              <div className="guide-feature-icon brown">
                <Image size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Logo</h4>
                <p>Votre logo de cabinet ou hôpital</p>
              </div>
            </div>
            <div className="guide-feature">
              <div className="guide-feature-icon blue">
                <FileText size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>En-tête</h4>
                <p>Nom, adresse, coordonnées, spécialité</p>
              </div>
            </div>
            <div className="guide-feature">
              <div className="guide-feature-icon purple">
                <FileText size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Pied de page</h4>
                <p>Mentions légales, numéro de page</p>
              </div>
            </div>
            <div className="guide-feature">
              <div className="guide-feature-icon cyan">
                <FileCode size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Style</h4>
                <p>Polices, couleurs, marges</p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </>
  );
}

export default TemplatesWordTab;
